export const ANIMATION_DURATIONS = {
  LISTENING_PULSE: 1500,
  LISTENING_VOLUME_REACT: 200,
  LISTENING_ENTRY: 300,
  LISTENING_EXIT: 200,

  THINKING_CYCLE: 2000,
  THINKING_COLLAPSE: 400,
  THINKING_ENTRY: 250,
  THINKING_HOLD: 2000,

  SPEAKING_RIPPLE: 2000,
  SPEAKING_VOLUME_REACT: 150,
  SPEAKING_ENTRY: 250,
  SPEAKING_EXIT: 200,

  STATE_TRANSITION: 300,
  MUTE_FADE: 200,
  UNMUTE_FADE: 250,
  CALL_END_FADEOUT: 600,
  REPLAY_RESTART: 150,
}

export const EASING = {
  SMOOTH_IN_OUT: 'cubic-bezier(0.4, 0, 0.2, 1)',
  SMOOTH_OUT: 'cubic-bezier(0.0, 0.0, 0.2, 1)',
  SMOOTH_IN: 'cubic-bezier(0.4, 0, 1, 1)',
  EASE_IN_OUT: 'ease-in-out',
  EASE_OUT: 'ease-out',
  EASE_IN: 'ease-in',
  GENTLE_EASE: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  SNAPPY: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
}

export const COLORS = {
  LISTENING_PRIMARY: 'rgba(0, 200, 255, 0.6)',
  LISTENING_SECONDARY: 'rgba(59, 130, 246, 0.3)',
  LISTENING_GLOW: 'rgba(0, 200, 255, 0.4)',
  LISTENING_DARK_GLOW: 'rgba(0, 150, 200, 0.2)',

  THINKING_PRIMARY: 'rgba(139, 92, 246, 0.5)',
  THINKING_SECONDARY: 'rgba(99, 102, 241, 0.3)',
  THINKING_GLOW: 'rgba(139, 92, 246, 0.3)',

  SPEAKING_PRIMARY: 'rgba(59, 130, 246, 0.7)',
  SPEAKING_SECONDARY: 'rgba(249, 115, 22, 0.5)',
  SPEAKING_GLOW: 'rgba(249, 115, 22, 0.4)',
  SPEAKING_ACCENT: 'rgba(248, 113, 113, 0.3)',
  MUTED_OVERLAY: 'rgba(0, 0, 0, 0.2)',
}

export const SCALES = {
  LISTENING_MIN: 1.0,
  LISTENING_MAX: 1.12,
  LISTENING_VOLUME_BOOST: 0.15,

  THINKING_COMPRESSED: 0.95,
  THINKING_NORMAL: 1.0,

  SPEAKING_MIN: 1.0,
  SPEAKING_MAX: 1.15,
  SPEAKING_VOLUME_BOOST: 0.12,

  RING_EXPAND_START: 1.0,
  RING_EXPAND_END: 2.0,
}

export const OPACITY = {
  LISTENING_ACTIVE: 0.8,
  LISTENING_PEAK: 1.0,
  LISTENING_LOW: 0.6,

  SPEAKING_START: 0.8,
  SPEAKING_MID: 0.4,
  SPEAKING_END: 0,

  THINKING_DIM: 0.4,
  THINKING_BRIGHT: 0.8,

  MUTED: 0.3,
  DISABLED: 0.5,
}

export const VOLUME_SENSITIVITY = {
  LISTENING_RESPONSIVENESS: 0.8,
  SPEAKING_RESPONSIVENESS: 0.9,

  LISTENING_THRESHOLD: 0.05,
  SPEAKING_THRESHOLD: 0.03,

  MAX_VOLUME: 1.0,
}

export const FILTERS = {
  LISTENING_BLUR: '0.5px',
  SPEAKING_BLUR_START: '0px',
  SPEAKING_BLUR_MID: '0.5px',
  SPEAKING_BLUR_END: '1.5px',
  THINKING_BLUR: '0.3px',
}

export enum VoiceAnimationState {
  IDLE = 'idle',
  LISTENING = 'listening',
  THINKING = 'thinking',
  SPEAKING = 'speaking',
  MUTED = 'muted',
  TRANSITIONING = 'transitioning',
}

export const ANIMATION_CONFIG = {
  listening: {
    duration: ANIMATION_DURATIONS.LISTENING_PULSE,
    easing: EASING.SMOOTH_IN_OUT,
    colors: [COLORS.LISTENING_PRIMARY, COLORS.LISTENING_SECONDARY],
    minScale: SCALES.LISTENING_MIN,
    maxScale: SCALES.LISTENING_MAX,
    opacityRange: [OPACITY.LISTENING_LOW, OPACITY.LISTENING_PEAK],
    description: 'Calm, steady pulse indicating active microphone',
  },
  thinking: {
    duration: ANIMATION_DURATIONS.THINKING_CYCLE,
    easing: EASING.GENTLE_EASE,
    colors: [COLORS.THINKING_PRIMARY, COLORS.THINKING_SECONDARY],
    minScale: SCALES.THINKING_COMPRESSED,
    maxScale: SCALES.THINKING_NORMAL,
    opacityRange: [OPACITY.THINKING_DIM, OPACITY.THINKING_BRIGHT],
    description: 'Subtle rotation/glow indicating AI is processing',
  },
  speaking: {
    duration: ANIMATION_DURATIONS.SPEAKING_RIPPLE,
    easing: EASING.SMOOTH_IN_OUT,
    colors: [COLORS.SPEAKING_PRIMARY, COLORS.SPEAKING_SECONDARY],
    minScale: SCALES.SPEAKING_MIN,
    maxScale: SCALES.SPEAKING_MAX,
    opacityRange: [OPACITY.SPEAKING_START, OPACITY.SPEAKING_END],
    description: 'Energetic, expanding ripples showing AI is speaking',
  },
}

export const STATE_TRANSITIONS = {
  LISTENING_TO_THINKING: {
    duration: ANIMATION_DURATIONS.THINKING_COLLAPSE,
    easing: EASING.SMOOTH_IN_OUT,
    type: 'collapse',
  },
  THINKING_TO_SPEAKING: {
    duration: ANIMATION_DURATIONS.SPEAKING_ENTRY,
    easing: EASING.SNAPPY,
    type: 'expand',
  },
  SPEAKING_TO_LISTENING: {
    duration: ANIMATION_DURATIONS.STATE_TRANSITION,
    easing: EASING.SMOOTH_IN_OUT,
    type: 'neutral',
  },
  ANY_TO_MUTED: {
    duration: ANIMATION_DURATIONS.MUTE_FADE,
    easing: EASING.EASE_OUT,
    type: 'fade',
  },
  MUTED_TO_ANY: {
    duration: ANIMATION_DURATIONS.UNMUTE_FADE,
    easing: EASING.EASE_IN,
    type: 'fade',
  },
}

export const prefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export const getReducedMotionDuration = (originalDuration: number): number => {
  if (!prefersReducedMotion()) return originalDuration
  return 50
}

export const getAnimationWithReducedMotion = (config: any) => {
  if (!prefersReducedMotion()) return config
  return {
    ...config,
    duration: 50,
    easing: 'linear',
  }
}

export const KEYFRAMES = {
  'listening-pulse': `
    0% {
      transform: translate(-50%, -50%) scale(${SCALES.LISTENING_MIN});
      opacity: ${OPACITY.LISTENING_ACTIVE};
      box-shadow: 0 0 8px ${COLORS.LISTENING_GLOW};
    }
    50% {
      transform: translate(-50%, -50%) scale(${SCALES.LISTENING_MAX});
      opacity: ${OPACITY.LISTENING_PEAK};
      box-shadow: 0 0 16px ${COLORS.LISTENING_GLOW}, 0 0 32px ${COLORS.LISTENING_DARK_GLOW};
    }
    100% {
      transform: translate(-50%, -50%) scale(${SCALES.LISTENING_MIN});
      opacity: ${OPACITY.LISTENING_ACTIVE};
      box-shadow: 0 0 8px ${COLORS.LISTENING_GLOW};
    }
  `,

  'listening-enter': `
    0% {
      opacity: 0;
      transform: scale(0.85);
    }
    100% {
      opacity: ${OPACITY.LISTENING_ACTIVE};
      transform: scale(1);
    }
  `,

  'listening-exit': `
    0% {
      opacity: ${OPACITY.LISTENING_ACTIVE};
      transform: scale(1);
    }
    100% {
      opacity: 0;
      transform: scale(0.9);
    }
  `,

  'thinking-rotate': `
    0% {
      transform: translate(-50%, -50%) rotate(0deg) scale(${SCALES.THINKING_NORMAL});
      opacity: ${OPACITY.THINKING_DIM};
    }
    50% {
      transform: translate(-50%, -50%) rotate(180deg) scale(${SCALES.THINKING_COMPRESSED});
      opacity: ${OPACITY.THINKING_BRIGHT};
    }
    100% {
      transform: translate(-50%, -50%) rotate(360deg) scale(${SCALES.THINKING_NORMAL});
      opacity: ${OPACITY.THINKING_DIM};
    }
  `,

  'thinking-collapse': `
    0% {
      transform: translate(-50%, -50%) scale(${SCALES.LISTENING_MAX});
      opacity: ${OPACITY.LISTENING_ACTIVE};
    }
    100% {
      transform: translate(-50%, -50%) scale(${SCALES.THINKING_COMPRESSED});
      opacity: ${OPACITY.THINKING_BRIGHT};
    }
  `,

  'thinking-enter': `
    0% {
      opacity: 0;
      transform: translate(-50%, -50%) scale(1.1);
    }
    100% {
      opacity: ${OPACITY.THINKING_BRIGHT};
      transform: translate(-50%, -50%) scale(${SCALES.THINKING_NORMAL});
    }
  `,

  'speaking-ripple': `
    0% {
      transform: translate(-50%, -50%) scale(${SCALES.RING_EXPAND_START});
      opacity: ${OPACITY.SPEAKING_START};
      filter: blur(${FILTERS.SPEAKING_BLUR_START});
    }
    40% {
      opacity: 0.4;
      filter: blur(${FILTERS.SPEAKING_BLUR_MID});
    }
    70% {
      transform: translate(-50%, -50%) scale(1.5);
      opacity: 0.1;
      filter: blur(${FILTERS.SPEAKING_BLUR_MID});
    }
    100% {
      transform: translate(-50%, -50%) scale(${SCALES.RING_EXPAND_END});
      opacity: ${OPACITY.SPEAKING_END};
      filter: blur(${FILTERS.SPEAKING_BLUR_END});
    }
  `,

  'speaking-enter': `
    0% {
      opacity: 0;
      transform: scale(0.9);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  `,

  'speaking-exit': `
    0% {
      opacity: 1;
      transform: scale(1);
    }
    100% {
      opacity: 0;
      transform: scale(0.95);
    }
  `,

  'muted-fade-in': `
    0% {
      opacity: 0;
    }
    100% {
      opacity: ${OPACITY.MUTED};
    }
  `,

  'muted-fade-out': `
    0% {
      opacity: ${OPACITY.MUTED};
    }
    100% {
      opacity: 0;
    }
  `,

  'call-end-fade': `
    0% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  `,

  'gentle-hover': `
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-8px);
    }
  `,
}

export const ANIMATION_CLASSES = {
  LISTENING: 'voice-state-listening',
  THINKING: 'voice-state-thinking',
  SPEAKING: 'voice-state-speaking',
  MUTED: 'voice-state-muted',

  ENTERING: 'voice-entering',
  EXITING: 'voice-exiting',

  VOLUME_REACTIVE: 'voice-volume-reactive',
}

export const getCSSAnimation = (
  keyframeName: string,
  duration: number = ANIMATION_DURATIONS.LISTENING_PULSE,
  easing: string = EASING.SMOOTH_IN_OUT,
  iterationCount: string | number = 'infinite',
  delay: number = 0
): string => {
  const reducedDuration = getReducedMotionDuration(duration)
  return `${keyframeName} ${reducedDuration}ms ${easing} ${delay}ms ${iterationCount}`
}

export const shouldAnimateForMotionPreference = (): boolean => {
  return !prefersReducedMotion()
}

export default {
  ANIMATION_DURATIONS,
  EASING,
  COLORS,
  SCALES,
  OPACITY,
  VOLUME_SENSITIVITY,
  FILTERS,
  VoiceAnimationState,
  ANIMATION_CONFIG,
  STATE_TRANSITIONS,
  KEYFRAMES,
  ANIMATION_CLASSES,
  prefersReducedMotion,
  getReducedMotionDuration,
  getAnimationWithReducedMotion,
  getCSSAnimation,
  shouldAnimateForMotionPreference,
}
